import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import TrialExpired from '../components/TrialExpired';
import { useConfig } from '../context/ConfigContext';
import usePullToRefresh from '../hooks/usePullToRefresh';
import usePagination from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import PhotoGalleryModal from '../components/PhotoGalleryModal';
import { PRODUCT_TYPE } from '../config/product';

function Routes() {
  const navigate = useNavigate();
  const { logout, isAdmin, isDriver, isSuperAdmin, isTrialExpired, shouldHidePrices } = useAuth();
  const { getAppName } = useConfig();

  const [routes, setRoutes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showStopsModal, setShowStopsModal] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [filterView, setFilterView] = useState('active'); // 'active', 'completed', 'archived'
  const [routeSortOrder, setRouteSortOrder] = useState(() => {
    return localStorage.getItem('lawntrack_routes_sort') || 'newest';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    serviceType: 'lawn_care',
    stormId: '',
    notes: '',
    assignedTo: '',
    driverPayType: '',
    driverPayRate: '',
    driverPayTotal: '',
    driverHoursWorked: '',
    startingAddress: '',
    startingCity: '',
    startingState: '',
    startingZip: '',
    endingAddress: '',
    endingCity: '',
    endingState: '',
    endingZip: '',
    endAtStart: true,
    invoiceTitle: '',
    invoiceMessage: '',
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [showInvoiceSettings, setShowInvoiceSettings] = useState(false);
  const [showDriverPaySettings, setShowDriverPaySettings] = useState(false);
  const [stopPhotoCounts, setStopPhotoCounts] = useState({});
  const [photoGalleryStopId, setPhotoGalleryStopId] = useState(null);
  const [driverFilter, setDriverFilter] = useState('all'); // 'all', 'unassigned', or a user ID
  const [paymentModal, setPaymentModal] = useState({ open: false, stop: null });
  const [paymentForm, setPaymentForm] = useState({
    paymentMethod: 'cash',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentNotes: ''
  });
  const [savingPayment, setSavingPayment] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [routesRes, customersRes, teamRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/routes?includeArchived=true&product=${PRODUCT_TYPE}`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/customers?active=true&includeChildren=true&product=${PRODUCT_TYPE}`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/team`),
      ]);
      setRoutes(routesRes.data);
      setCustomers(customersRes.data);
      setTeamMembers(teamRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Toggle route sort order and save to localStorage
  const toggleRouteSortOrder = () => {
    const newOrder = routeSortOrder === 'newest' ? 'oldest' : 'newest';
    setRouteSortOrder(newOrder);
    localStorage.setItem('lawntrack_routes_sort', newOrder);
  };

  // Filter and sort routes based on filter view, driver filter, and sort order
  const filteredRoutes = routes
    .filter(route => {
      // Status filter
      if (filterView === 'archived') {
        if (route.is_archived !== true) return false;
      } else if (filterView === 'completed') {
        if (route.is_archived || route.status !== 'completed') return false;
      } else {
        // 'active' - show not_started and in_progress (non-archived, non-completed)
        if (route.is_archived || route.status === 'completed') return false;
      }

      // Driver filter
      if (driverFilter !== 'all') {
        if (driverFilter === 'unassigned') {
          if (route.assigned_to) return false;
        } else {
          // Filter by specific driver ID
          if (String(route.assigned_to) !== String(driverFilter)) return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return routeSortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

  // Pagination for routes (mainly useful for completed/archived views)
  const {
    paginatedData: paginatedRoutes,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalItems: totalRoutes
  } = usePagination(filteredRoutes, 'routes', 25);

  const { PullToRefreshIndicator } = usePullToRefresh(loadData);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openAddModal = () => {
    // Check if trial user has exceeded their limit
    if (isTrialExpired()) {
      setShowTrialModal(true);
      return;
    }
    setEditingRoute(null);
    setFormData({
      name: '',
      serviceType: 'lawn_care',
      stormId: '',
      notes: '',
      assignedTo: '',
      driverPayType: '',
      driverPayRate: '',
      driverPayTotal: '',
      driverHoursWorked: '',
      startingAddress: '',
      startingCity: '',
      startingState: '',
      startingZip: '',
      endingAddress: '',
      endingCity: '',
      endingState: '',
      endingZip: '',
      endAtStart: true,
      invoiceTitle: '',
      invoiceMessage: '',
    });
    setShowInvoiceSettings(false);
    setShowDriverPaySettings(false);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (route) => {
    setEditingRoute(route);
    setFormData({
      name: route.name || '',
      serviceType: route.service_type || 'lawn_care',
      stormId: route.storm_id || '',
      notes: route.notes || '',
      assignedTo: route.assigned_to || '',
      driverPayType: route.driver_pay_type || '',
      driverPayRate: route.driver_pay_rate || '',
      driverPayTotal: route.driver_pay_total || '',
      driverHoursWorked: route.driver_hours_worked || '',
      startingAddress: route.starting_address || '',
      startingCity: route.starting_city || '',
      startingState: route.starting_state || '',
      startingZip: route.starting_zip || '',
      endingAddress: route.ending_address || '',
      endingCity: route.ending_city || '',
      endingState: route.ending_state || '',
      endingZip: route.ending_zip || '',
      endAtStart: route.end_at_start ?? true,
      invoiceTitle: route.invoice_title || '',
      invoiceMessage: route.invoice_message || '',
    });
    setShowInvoiceSettings(!!(route.invoice_title || route.invoice_message));
    setShowDriverPaySettings(!!(route.driver_pay_type || route.driver_pay_rate));
    setError('');
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      // Calculate driver pay total based on pay type
      let calculatedPayTotal = formData.driverPayTotal || null;
      if (formData.driverPayType === 'hourly' && formData.driverPayRate && formData.driverHoursWorked) {
        calculatedPayTotal = parseFloat(formData.driverPayRate) * parseFloat(formData.driverHoursWorked);
      } else if (formData.driverPayType === 'flat' && formData.driverPayRate) {
        calculatedPayTotal = parseFloat(formData.driverPayRate);
      } else if (formData.driverPayType === 'per_stop' && formData.driverPayRate && editingRoute?.stop_count) {
        calculatedPayTotal = parseFloat(formData.driverPayRate) * parseInt(editingRoute.stop_count);
      }

      const payload = {
        name: formData.name,
        serviceType: formData.serviceType,
        stormId: formData.stormId,
        notes: formData.notes,
        assignedTo: formData.assignedTo || null,
        startingAddress: formData.startingAddress,
        startingCity: formData.startingCity,
        startingState: formData.startingState,
        startingZip: formData.startingZip,
        endingAddress: formData.endAtStart ? formData.startingAddress : formData.endingAddress,
        endingCity: formData.endAtStart ? formData.startingCity : formData.endingCity,
        endingState: formData.endAtStart ? formData.startingState : formData.endingState,
        endingZip: formData.endAtStart ? formData.startingZip : formData.endingZip,
        endAtStart: formData.endAtStart,
        invoiceTitle: formData.invoiceTitle || null,
        invoiceMessage: formData.invoiceMessage || null,
        driverPayType: formData.driverPayType || null,
        driverPayRate: formData.driverPayRate || null,
        driverPayTotal: calculatedPayTotal,
        driverHoursWorked: formData.driverHoursWorked || null,
        productType: PRODUCT_TYPE, // Ensure lawn routes are created with correct product_type
      };

      if (editingRoute) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/routes/${editingRoute.id}`,
          payload
        );
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/routes`, payload);
      }

      setShowModal(false);
      loadData();
    } catch (err) {
      // Check for trial expired error
      if (err.response?.data?.code === 'TRIAL_EXPIRED') {
        setShowModal(false);
        setShowTrialModal(true);
        return;
      }
      setError(err.response?.data?.error || 'Failed to save route');
    } finally {
      setSaving(false);
    }
  };

  const deleteRoute = async (route) => {
    if (!window.confirm(`Delete "${route.name}"? This will permanently remove the route and all its stops. This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/routes/${route.id}`);
      loadData();
      setShowModal(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete route');
    }
  };

  const archiveRoute = async (route) => {
    const action = route.is_archived ? 'restore' : 'archive';
    if (!window.confirm(`${route.is_archived ? 'Restore' : 'Archive'} "${route.name}"?`)) {
      return;
    }

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/routes/${route.id}`, {
        ...route,
        isArchived: !route.is_archived,
      });
      loadData();
      alert(`Route ${action}d successfully`);
    } catch (err) {
      alert(err.response?.data?.error || `Failed to ${action} route`);
    }
  };

  const openStopsModal = async (route) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${route.id}`
      );
      setSelectedRoute(response.data);
      setShowStopsModal(true);

      // Load photo counts for completed stops
      const completedStops = response.data.stops?.filter(s => s.status === 'completed') || [];
      if (completedStops.length > 0) {
        const stopIds = completedStops.map(s => s.id);
        try {
          const countsRes = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/route-stops/photo-counts`,
            { stopIds }
          );
          setStopPhotoCounts(countsRes.data);
        } catch (photoErr) {
          console.error('Failed to load photo counts:', photoErr);
        }
      }
    } catch (err) {
      console.error('Failed to load route details:', err);
    }
  };

  const addStopToRoute = async (customerId) => {
    if (!selectedRoute) return;

    try {
      const currentStopCount = selectedRoute.stops?.length || 0;
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops`,
        {
          customerId,
          stopOrder: currentStopCount + 1,
        }
      );
      // Reload route details
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);
      loadData(); // Refresh routes list
    } catch (err) {
      alert('Failed to add stop');
    }
  };

  const removeStopFromRoute = async (stopId) => {
    if (!selectedRoute) return;
    if (!window.confirm('Remove this stop from the route?')) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stopId}`
      );
      // Reload route details
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);
      loadData();
    } catch (err) {
      alert('Failed to remove stop');
    }
  };

  // Open payment modal for a stop
  const openPaymentModal = (stop) => {
    setPaymentForm({
      paymentMethod: 'cash',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentNotes: ''
    });
    setPaymentModal({ open: true, stop });
  };

  // Submit payment update
  const submitPayment = async () => {
    if (!paymentModal.stop?.id) return;

    setSavingPayment(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/route-stops/${paymentModal.stop.id}/payment`,
        {
          paymentStatus: 'paid',
          paymentMethod: paymentForm.paymentMethod,
          paymentDate: paymentForm.paymentDate,
          paymentNotes: paymentForm.paymentNotes || undefined
        }
      );
      setPaymentModal({ open: false, stop: null });
      // Reload route details
      if (selectedRoute) {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
        );
        setSelectedRoute(response.data);
      }
      loadData();
    } catch (err) {
      console.error('Failed to update payment:', err);
      alert('Failed to update payment status');
    } finally {
      setSavingPayment(false);
    }
  };

  // Flatten customers and their child properties into a single list of stops
  const getAllStopOptions = () => {
    const stopOptions = [];
    for (const customer of customers) {
      // Add parent customer as a stop option
      stopOptions.push({
        id: customer.id,
        displayName: `${customer.first_name} ${customer.last_name}`,
        address: customer.address,
        city: customer.city,
        service_frequency: customer.service_frequency,
        snowfall_threshold: customer.snowfall_threshold,
        isChild: false,
      });
      // Add child properties as separate stop options
      if (customer.properties && customer.properties.length > 0) {
        for (const prop of customer.properties) {
          if (prop.is_active) {
            stopOptions.push({
              id: prop.id,
              displayName: `${customer.first_name} ${customer.last_name} - ${prop.address}`,
              address: prop.address,
              city: prop.city,
              service_frequency: prop.service_frequency,
              snowfall_threshold: customer.snowfall_threshold, // Child inherits from parent
              isChild: true,
              parentName: `${customer.first_name} ${customer.last_name}`,
            });
          }
        }
      }
    }
    return stopOptions;
  };

  const getCustomersNotInRoute = () => {
    const allStops = getAllStopOptions();
    if (!selectedRoute || !selectedRoute.stops) return allStops;
    const stopCustomerIds = selectedRoute.stops.map((s) => s.customer_id);
    return allStops.filter((s) => !stopCustomerIds.includes(s.id));
  };

  const [optimizing, setOptimizing] = useState(false);

  // Route optimization using Google Maps Distance Matrix API
  const optimizeRouteOrder = async () => {
    if (!selectedRoute?.stops?.length) return;

    setOptimizing(true);
    try {
      // Call the server endpoint for Google Maps optimization
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/optimize`
      );

      // Reload route to get new order
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);
      alert('Route optimized using Google Maps! Stops are now in the most efficient order.');
    } catch (err) {
      console.error('Failed to optimize route:', err);
      // Fall back to simple sort if Google Maps fails
      if (err.response?.status === 500) {
        alert('Google Maps optimization failed. Using basic sorting instead.');
        await fallbackOptimize();
      } else {
        alert('Failed to optimize route order');
      }
    } finally {
      setOptimizing(false);
    }
  };

  // Fallback to simple sort if Google Maps API fails
  const fallbackOptimize = async () => {
    const sortedStops = [...selectedRoute.stops].sort((a, b) => {
      const cityCompare = (a.city || '').localeCompare(b.city || '');
      if (cityCompare !== 0) return cityCompare;
      const zipCompare = (a.zip || '').localeCompare(b.zip || '');
      if (zipCompare !== 0) return zipCompare;
      const streetA = (a.address || '').replace(/^\d+\s*/, '');
      const streetB = (b.address || '').replace(/^\d+\s*/, '');
      return streetA.localeCompare(streetB);
    });

    try {
      for (let i = 0; i < sortedStops.length; i++) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${sortedStops[i].id}`,
          { stopOrder: i + 1 }
        );
      }
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);
    } catch (err) {
      console.error('Fallback optimization failed:', err);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'in_progress':
        return 'status-badge in-progress';
      case 'completed':
        return 'status-badge completed';
      default:
        return 'status-badge not-started';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      default:
        return 'Not Started';
    }
  };

  return (
    <div className="routes-page">
      {/* Trial Expired Modal */}
      {showTrialModal && (
        <TrialExpired
          variant="modal"
          onClose={() => setShowTrialModal(false)}
        />
      )}
      {/* Header */}
      <header className="app-header">
        <div className="app-header-content">
          <span className="app-logo" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
            {getAppName()}
          </span>
          <nav className="header-nav desktop-nav">
            <button onClick={() => navigate('/dashboard')} className="nav-link">Dashboard</button>
            {!isDriver() && <button onClick={() => navigate('/customers')} className="nav-link">Customers</button>}
            <span className="nav-link active">Routes</span>
            {isAdmin() && <button onClick={() => navigate('/history')} className="nav-link">History</button>}
            {isAdmin() && <button onClick={() => navigate('/expenses')} className="nav-link">Expenses</button>}
            {isAdmin() && <button onClick={() => navigate('/reports')} className="nav-link">Reports</button>}
            <button onClick={() => navigate('/service-mode')} className="nav-link storm-link">Service Mode</button>
            <button onClick={() => navigate('/settings')} className="nav-link">Settings</button>
            <button onClick={() => navigate('/docs')} className="nav-link help-link" title="Help">?</button>
            {isSuperAdmin() && (
              <button onClick={() => navigate('/admin')} className="nav-link admin-link">Admin</button>
            )}
            <button onClick={logout} className="nav-link logout-link">Logout</button>
          </nav>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
        {mobileMenuOpen && (
          <nav className="mobile-nav">
            <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="mobile-nav-link">Dashboard</button>
            {!isDriver() && <button onClick={() => { navigate('/customers'); setMobileMenuOpen(false); }} className="mobile-nav-link">Customers</button>}
            <span className="mobile-nav-link active">Routes</span>
            {isAdmin() && <button onClick={() => { navigate('/history'); setMobileMenuOpen(false); }} className="mobile-nav-link">History</button>}
            {isAdmin() && <button onClick={() => { navigate('/expenses'); setMobileMenuOpen(false); }} className="mobile-nav-link">Expenses</button>}
            {isAdmin() && <button onClick={() => { navigate('/reports'); setMobileMenuOpen(false); }} className="mobile-nav-link">Reports</button>}
            <button onClick={() => { navigate('/service-mode'); setMobileMenuOpen(false); }} className="mobile-nav-link storm-link">Service Mode</button>
            <button onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }} className="mobile-nav-link">Settings</button>
            <button onClick={() => { navigate('/docs'); setMobileMenuOpen(false); }} className="mobile-nav-link">Help</button>
            {isSuperAdmin() && (
              <button onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }} className="mobile-nav-link admin-link">Admin</button>
            )}
            <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="mobile-nav-link logout-link">Logout</button>
          </nav>
        )}
      </header>

      <PullToRefreshIndicator />

      <div className="page-container">
        {/* Page Header */}
        <div className="page-header flex-between">
          <div>
            <h1 className="page-title">Routes</h1>
            <p className="text-muted">{filteredRoutes.length} {filterView} route{filteredRoutes.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="header-actions">
            {isAdmin() && filterView === 'active' && (
              <button className="btn btn-primary" onClick={openAddModal}>
                + New Route
              </button>
            )}
          </div>
        </div>

        {/* Filter and Sort Controls */}
        <div className="routes-controls">
          <div className="archive-toggle">
            <button
              className={`toggle-btn ${filterView === 'active' ? 'active' : ''}`}
              onClick={() => setFilterView('active')}
            >
              Active
            </button>
            <button
              className={`toggle-btn ${filterView === 'completed' ? 'active' : ''}`}
              onClick={() => setFilterView('completed')}
            >
              Completed
            </button>
            <button
              className={`toggle-btn ${filterView === 'archived' ? 'active' : ''}`}
              onClick={() => setFilterView('archived')}
            >
              Archived
            </button>
          </div>
          <button className="sort-toggle" onClick={toggleRouteSortOrder}>
            {routeSortOrder === 'newest' ? '↓ Newest' : '↑ Oldest'}
          </button>
          {isAdmin() && teamMembers.length > 0 && (
            <select
              className="driver-filter-select"
              value={driverFilter}
              onChange={(e) => setDriverFilter(e.target.value)}
            >
              <option value="all">All Drivers</option>
              <option value="unassigned">Unassigned</option>
              {teamMembers.map(member => (
                <option key={member.id} value={member.id}>
                  {member.first_name} {member.last_name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Routes List */}
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : filteredRoutes.length === 0 ? (
          <div className="empty-state">
            <p>
              {filterView === 'archived' && 'No archived routes'}
              {filterView === 'completed' && 'No completed routes'}
              {filterView === 'active' && 'No active routes'}
            </p>
            {isAdmin() && filterView === 'active' && routes.length === 0 && (
              <button className="btn btn-primary" onClick={openAddModal}>
                Create your first route
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="routes-list">
              {paginatedRoutes.map((route) => (
              <div key={route.id} className={`route-card ${route.is_archived ? 'archived' : ''}`}>
                <div className="route-card-header">
                  <div className="route-info">
                    <h3 className="route-name">
                      {route.name}
                      {route.is_archived && <span className="archived-badge">Archived</span>}
                    </h3>
                    <div className="route-meta">
                      <span className={getStatusBadgeClass(route.status)}>
                        {getStatusLabel(route.status)}
                      </span>
                      {route.storm_id && <span className="storm-id-badge">Visit: {route.storm_id}</span>}
                      {route.assigned_to_name ? (
                        <span className="assigned-badge">{route.assigned_to_name}</span>
                      ) : isAdmin() && (
                        <span className="assigned-badge unassigned">Unassigned</span>
                      )}
                      <span className="stop-count">{route.stop_count || 0} stops</span>
                      {!shouldHidePrices() && (
                        <span className="route-total-wrapper">
                          <span className="route-total">
                            ${parseFloat(route.total_price || 0).toFixed(2)}
                          </span>
                          {parseInt(route.skipped_count) > 0 && (
                            <span className="skipped-indicator">({route.skipped_count} skipped)</span>
                          )}
                        </span>
                      )}
                      {isDriver() && route.driver_pay_type && (route.driver_pay_total || route.driver_pay_rate) && (
                        <span className="driver-pay-badge">
                          Your pay: {route.driver_pay_total
                            ? `$${parseFloat(route.driver_pay_total).toFixed(2)}`
                            : route.driver_pay_type === 'flat'
                              ? `$${parseFloat(route.driver_pay_rate).toFixed(2)}`
                              : route.driver_pay_type === 'hourly'
                                ? `$${parseFloat(route.driver_pay_rate).toFixed(2)}/hr`
                                : `$${parseFloat(route.driver_pay_rate).toFixed(2)}/stop`
                          }
                        </span>
                      )}
                      {/* Payment breakdown for completed routes */}
                      {!shouldHidePrices() && route.status === 'completed' && parseFloat(route.total_price || 0) > 0 && (
                        <span className="payment-breakdown">
                          {parseFloat(route.total_unpaid || 0) === 0 ? (
                            <span className="payment-fully-paid">✓ Paid</span>
                          ) : parseFloat(route.total_paid || 0) === 0 ? (
                            <span className="payment-unpaid">${parseFloat(route.total_unpaid || 0).toFixed(2)} unpaid</span>
                          ) : (
                            <>
                              <span className="payment-paid">${parseFloat(route.total_paid || 0).toFixed(2)}</span>
                              <span className="payment-separator">|</span>
                              <span className="payment-unpaid">${parseFloat(route.total_unpaid || 0).toFixed(2)}</span>
                            </>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="route-actions">
                    {!route.is_archived ? (
                      <>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => navigate('/service-mode', { state: { routeId: route.id } })}
                        >
                          {route.status === 'completed' ? 'View' : route.status === 'in_progress' ? 'Continue' : 'Start'}
                        </button>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => openStopsModal(route)}
                        >
                          Stops
                        </button>
                        {isAdmin() && (
                          <>
                            <button
                              className="btn btn-sm btn-outline"
                              onClick={() => openEditModal(route)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-outline"
                              onClick={() => archiveRoute(route)}
                              title="Archive route"
                            >
                              Archive
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => deleteRoute(route)}
                              title="Delete route"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </>
                    ) : (
                      /* Archived route actions */
                      <>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => openStopsModal(route)}
                        >
                          View Stops
                        </button>
                        {isAdmin() && (
                          <>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => archiveRoute(route)}
                            >
                              Restore
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => deleteRoute(route)}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {route.notes && (
                  <p className="route-notes">{route.notes}</p>
                )}
              </div>
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalItems={totalRoutes}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </>
        )}
      </div>

      {/* Add/Edit Route Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRoute ? 'Edit Route' : 'New Route'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>

            <form onSubmit={handleSubmit}>
              {error && <div className="alert alert-error">{error}</div>}

              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Route Name *</label>
                  <input
                    type="text"
                    name="name"
                    className="form-input"
                    value={formData.name}
                    onChange={handleFormChange}
                    placeholder="e.g., North Side, Downtown, etc."
                    required
                  />
                </div>

                {/* Service Type - Lawn Care only for this app */}
                <input type="hidden" name="serviceType" value="lawn_care" />

                <div className="form-group">
                  <label className="form-label">Visit ID</label>
                  <input
                    type="text"
                    name="stormId"
                    className="form-input"
                    value={formData.stormId}
                    onChange={handleFormChange}
                    placeholder="e.g., 11.29.25"
                  />
                  <small className="text-muted">Group routes by visit for referral discounts</small>
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    name="notes"
                    className="form-textarea"
                    value={formData.notes}
                    onChange={handleFormChange}
                    rows={3}
                    placeholder="Any special instructions for this route..."
                  />
                </div>

                {teamMembers.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Assign To</label>
                    <select
                      name="assignedTo"
                      className="form-select"
                      value={formData.assignedTo}
                      onChange={handleFormChange}
                    >
                      <option value="">Unassigned</option>
                      {teamMembers.map(member => (
                        <option key={member.id} value={member.id}>
                          {member.first_name} {member.last_name}
                          {member.role && ` (${member.role.replace('_', ' ')})`}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">Assign this route to a team member</small>
                  </div>
                )}

                {/* Driver Pay Settings - Owner, Admin, and Crew Lead */}
                {(isAdmin() || (!isDriver())) && (
                  <>
                    <div
                      className="form-divider collapsible-header"
                      onClick={() => setShowDriverPaySettings(!showDriverPaySettings)}
                      style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <span>Driver Pay (Optional)</span>
                      <span style={{ fontSize: '0.8rem' }}>{showDriverPaySettings ? '▼' : '▶'}</span>
                    </div>

                    {showDriverPaySettings && (
                      <>
                        <p className="form-hint">Set driver pay for this route. Drivers will see their earnings on their route history.</p>

                        <div className="form-row">
                          <div className="form-group">
                            <label className="form-label">Pay Type</label>
                            <select
                              name="driverPayType"
                              className="form-select"
                              value={formData.driverPayType}
                              onChange={handleFormChange}
                            >
                              <option value="">None</option>
                              <option value="flat">Flat Rate</option>
                              <option value="hourly">Hourly</option>
                              <option value="per_stop">Per Stop</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label className="form-label">
                              {formData.driverPayType === 'hourly' ? 'Hourly Rate ($)' :
                               formData.driverPayType === 'per_stop' ? 'Rate Per Stop ($)' :
                               'Pay Rate ($)'}
                            </label>
                            <input
                              type="number"
                              name="driverPayRate"
                              className="form-input"
                              value={formData.driverPayRate}
                              onChange={handleFormChange}
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              disabled={!formData.driverPayType}
                            />
                          </div>
                        </div>

                        {formData.driverPayType === 'hourly' && (
                          <div className="form-row">
                            <div className="form-group">
                              <label className="form-label">Hours Worked</label>
                              <input
                                type="number"
                                name="driverHoursWorked"
                                className="form-input"
                                value={formData.driverHoursWorked}
                                onChange={handleFormChange}
                                placeholder="0.0"
                                step="0.25"
                                min="0"
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Calculated Total</label>
                              <div className="calculated-total">
                                ${formData.driverPayRate && formData.driverHoursWorked
                                  ? (parseFloat(formData.driverPayRate) * parseFloat(formData.driverHoursWorked)).toFixed(2)
                                  : '0.00'}
                              </div>
                            </div>
                          </div>
                        )}

                        {formData.driverPayType === 'flat' && formData.driverPayRate && (
                          <div className="form-group">
                            <label className="form-label">Total Pay</label>
                            <div className="calculated-total">
                              ${parseFloat(formData.driverPayRate).toFixed(2)}
                            </div>
                          </div>
                        )}

                        {formData.driverPayType === 'per_stop' && formData.driverPayRate && editingRoute && (
                          <div className="form-group">
                            <label className="form-label">Estimated Total ({editingRoute.stop_count || 0} stops)</label>
                            <div className="calculated-total">
                              ${(parseFloat(formData.driverPayRate) * (parseInt(editingRoute.stop_count) || 0)).toFixed(2)}
                            </div>
                            <small className="text-muted">Final total calculated based on completed stops</small>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                <div className="form-divider">Starting Location</div>
                <p className="form-hint">Where does the driver start from? (home, shop, etc.)</p>

                <div className="form-group">
                  <label className="form-label">Starting Address</label>
                  <input
                    type="text"
                    name="startingAddress"
                    className="form-input"
                    value={formData.startingAddress}
                    onChange={handleFormChange}
                    placeholder="123 Main St"
                  />
                </div>

                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input
                      type="text"
                      name="startingCity"
                      className="form-input"
                      value={formData.startingCity}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State</label>
                    <input
                      type="text"
                      name="startingState"
                      className="form-input"
                      value={formData.startingState}
                      onChange={handleFormChange}
                      maxLength={2}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">ZIP</label>
                    <input
                      type="text"
                      name="startingZip"
                      className="form-input"
                      value={formData.startingZip}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                <div className="form-divider">Ending Location</div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="endAtStart"
                      checked={formData.endAtStart}
                      onChange={handleFormChange}
                    />
                    End at starting location
                  </label>
                </div>

                {!formData.endAtStart && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Ending Address</label>
                      <input
                        type="text"
                        name="endingAddress"
                        className="form-input"
                        value={formData.endingAddress}
                        onChange={handleFormChange}
                        placeholder="456 Other St"
                      />
                    </div>

                    <div className="form-row form-row-3">
                      <div className="form-group">
                        <label className="form-label">City</label>
                        <input
                          type="text"
                          name="endingCity"
                          className="form-input"
                          value={formData.endingCity}
                          onChange={handleFormChange}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">State</label>
                        <input
                          type="text"
                          name="endingState"
                          className="form-input"
                          value={formData.endingState}
                          onChange={handleFormChange}
                          maxLength={2}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">ZIP</label>
                        <input
                          type="text"
                          name="endingZip"
                          className="form-input"
                          value={formData.endingZip}
                          onChange={handleFormChange}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Invoice Settings - Collapsible */}
                <div
                  className="form-divider collapsible-header"
                  onClick={() => setShowInvoiceSettings(!showInvoiceSettings)}
                  style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span>Invoice Settings (Optional)</span>
                  <span style={{ fontSize: '0.8rem' }}>{showInvoiceSettings ? '▼' : '▶'}</span>
                </div>

                {showInvoiceSettings && (
                  <>
                    <p className="form-hint">Customize invoice title and message for this route. Leave blank to use company defaults.</p>

                    <div className="form-group">
                      <label className="form-label">Invoice Title</label>
                      <input
                        type="text"
                        name="invoiceTitle"
                        className="form-input"
                        value={formData.invoiceTitle}
                        onChange={handleFormChange}
                        placeholder="e.g., Lawn Service - North Side (defaults to route name)"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Invoice Message</label>
                      <textarea
                        name="invoiceMessage"
                        className="form-textarea"
                        value={formData.invoiceMessage}
                        onChange={handleFormChange}
                        rows={3}
                        placeholder="Thank you message for customers..."
                      />
                      <small className="text-muted">
                        Variables: {'{route_name}'}, {'{service_date}'}, {'{customer_name}'}
                      </small>
                    </div>
                  </>
                )}

              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingRoute ? 'Save Changes' : 'Create Route'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Stops Modal */}
      {showStopsModal && selectedRoute && (
        <div className="modal-overlay" onClick={() => setShowStopsModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage Stops - {selectedRoute.name}</h2>
              <button className="modal-close" onClick={() => setShowStopsModal(false)}>&times;</button>
            </div>

            <div className="modal-body stops-modal-body">
              {/* Current Stops */}
              <div className="stops-section">
                <div className="stops-section-header">
                  <h3 className="stops-section-title">
                    Current Stops ({selectedRoute.stops?.length || 0})
                  </h3>
                  {selectedRoute.stops?.length > 1 && (
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={optimizeRouteOrder}
                      disabled={optimizing}
                    >
                      {optimizing ? 'Optimizing...' : 'Optimize Route'}
                    </button>
                  )}
                </div>
                {selectedRoute.stops?.length === 0 ? (
                  <p className="text-muted">No stops added yet. Add customers from the list below.</p>
                ) : (
                  <div className="stops-list">
                    {selectedRoute.stops?.map((stop, index) => (
                      <div key={stop.id} className="stop-item">
                        <div className="stop-order">{index + 1}</div>
                        <div className="stop-info">
                          <div className="stop-name">
                            {stop.first_name} {stop.last_name}
                          </div>
                          <div className="stop-address">{stop.address}, {stop.city}</div>
                        </div>
                        {stop.status === 'completed' && (
                          <>
                            <span className={`payment-badge ${
                              stop.payment_status === 'paid' ? 'paid' :
                              stop.payment_status === 'invoice_sent' ? 'invoice-sent' : 'not-paid'
                            }`}>
                              {stop.payment_status === 'paid'
                                ? `${stop.payment_method === 'square' ? 'Square' : stop.payment_method === 'venmo' ? 'Venmo' : 'Cash'}`
                                : stop.payment_status === 'invoice_sent'
                                ? 'Invoice Sent'
                                : 'Not Paid'}
                            </span>
                            {stop.payment_status !== 'paid' && !stop.is_comp && (
                              <span
                                className="mark-paid-link"
                                onClick={() => openPaymentModal(stop)}
                              >
                                Mark Paid
                              </span>
                            )}
                          </>
                        )}
                        {stop.is_comp && (
                          <span className="comp-badge" title={stop.comp_reason || 'Comp/Free'}>
                            COMP
                          </span>
                        )}
                        {stopPhotoCounts[stop.id] > 0 && (
                          <span
                            className="photo-count-badge clickable"
                            title={`${stopPhotoCounts[stop.id]} photo${stopPhotoCounts[stop.id] > 1 ? 's' : ''} - Click to view`}
                            onClick={() => setPhotoGalleryStopId(stop.id)}
                          >
                            📷 {stopPhotoCounts[stop.id]}
                          </span>
                        )}
                        <button
                          className="remove-stop-btn"
                          onClick={() => removeStopFromRoute(stop.id)}
                          title="Remove from route"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Available Customers */}
              <div className="stops-section">
                <h3 className="stops-section-title">
                  Available Customers ({getCustomersNotInRoute().length})
                </h3>
                {getCustomersNotInRoute().length === 0 ? (
                  <p className="text-muted">All customers have been added to this route.</p>
                ) : (
                  <div className="available-customers-list">
                    {getCustomersNotInRoute().map((stopOption) => (
                      <div key={stopOption.id} className="available-customer-item">
                        <div className="customer-info">
                          <div className="customer-name">
                            {stopOption.displayName}
                            <span className={`service-type-badge ${stopOption.service_frequency === 'as_needed' ? 'as-needed' : 'seasonal'}`}>
                              {stopOption.service_frequency === 'as_needed' ? 'As Needed' : 'Seasonal'}
                            </span>
                          </div>
                          <div className="customer-address">{stopOption.address}, {stopOption.city}</div>
                        </div>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => addStopToRoute(stopOption.id)}
                        >
                          + Add
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowStopsModal(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-link {
          background: var(--color-secondary) !important;
          color: white !important;
        }
        .storm-link:hover {
          background: var(--color-secondary-dark) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }

        .flex-between {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 16px;
        }

        .empty-state {
          text-align: center;
          padding: 48px 16px;
          color: var(--color-text-light);
        }
        .empty-state .btn {
          margin-top: 16px;
        }

        .routes-list {
          display: grid;
          gap: 16px;
        }

        .route-card {
          background: var(--color-card);
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .route-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          flex-wrap: wrap;
        }

        .route-name {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0 0 8px 0;
        }

        .route-meta {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .status-badge {
          font-size: 0.75rem;
          padding: 4px 10px;
          border-radius: 12px;
          font-weight: 500;
        }
        .status-badge.not-started {
          background: #e5e7eb;
          color: #4b5563;
        }
        .status-badge.in-progress {
          background: #fef3c7;
          color: #92400e;
        }
        .status-badge.completed {
          background: #d1fae5;
          color: #065f46;
        }

        .stop-count {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }

        .route-total {
          font-size: 1rem;
          font-weight: 700;
          color: var(--color-success);
          background: rgba(76, 175, 80, 0.1);
          padding: 4px 10px;
          border-radius: 6px;
        }
        .driver-pay-badge {
          font-size: 0.9rem;
          font-weight: 600;
          color: #2196F3;
          background: rgba(33, 150, 243, 0.1);
          padding: 4px 10px;
          border-radius: 6px;
        }
        .calculated-total {
          font-size: 1.25rem;
          font-weight: 700;
          color: #166534;
          background: rgba(22, 101, 52, 0.1);
          padding: 10px 16px;
          border-radius: 8px;
        }
        .route-total-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .skipped-indicator {
          font-size: 0.75rem;
          font-weight: 500;
          color: #6b7280;
        }

        /* Payment breakdown styles */
        .payment-breakdown {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.8rem;
          font-weight: 500;
          padding: 3px 8px;
          border-radius: 4px;
          background: rgba(0, 0, 0, 0.03);
        }
        .payment-fully-paid {
          color: #10b981;
          font-weight: 600;
        }
        .payment-paid {
          color: #10b981;
        }
        .payment-unpaid {
          color: #f59e0b;
        }
        .payment-separator {
          color: #d1d5db;
          margin: 0 2px;
        }

        .route-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .route-notes {
          margin: 12px 0 0;
          padding-top: 12px;
          border-top: 1px solid var(--color-border);
          font-size: 0.875rem;
          color: var(--color-text-light);
        }

        /* Form Styles */
        .form-divider {
          font-weight: 600;
          color: var(--color-text);
          margin: 20px 0 8px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--color-border);
        }
        .form-hint {
          font-size: 0.875rem;
          color: var(--color-text-light);
          margin: 0 0 12px 0;
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .form-row-3 {
          grid-template-columns: 2fr 1fr 1fr;
        }
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 0.875rem;
        }
        .checkbox-label input {
          width: 18px;
          height: 18px;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
        }
        .modal {
          background: white;
          border-radius: 12px;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-large {
          max-width: 700px;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid var(--color-border);
        }
        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: var(--color-text-light);
          line-height: 1;
        }
        .modal-body {
          padding: 20px;
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 20px;
          border-top: 1px solid var(--color-border);
        }

        /* Stops Modal */
        .stops-modal-body {
          max-height: 60vh;
          overflow-y: auto;
        }
        .stops-section {
          margin-bottom: 24px;
        }
        .stops-section:last-child {
          margin-bottom: 0;
        }
        .stops-section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }
        .stops-section-title {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
          color: var(--color-text);
        }

        .stops-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .stop-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 12px;
          background: var(--color-background);
          border: 1px solid var(--color-border);
          border-radius: 8px;
        }
        .stop-order {
          width: 32px;
          height: 32px;
          background: var(--color-primary);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.875rem;
          flex-shrink: 0;
        }
        .stop-info {
          flex: 1;
        }
        .stop-name {
          font-weight: 500;
        }
        .stop-address {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }

        /* Remove stop button - red X */
        .remove-stop-btn {
          width: 40px;
          height: 40px;
          min-width: 40px;
          border: 2px solid #dc2626;
          background: #fee2e2;
          color: #dc2626;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s ease;
          flex-shrink: 0;
          margin-left: auto;
        }
        .remove-stop-btn:hover {
          background: #dc2626;
          color: white;
          transform: scale(1.1);
        }
        .remove-stop-btn:active {
          transform: scale(0.95);
        }
        .remove-stop-btn svg {
          width: 20px;
          height: 20px;
        }

        /* Payment Badge */
        .payment-badge {
          font-size: 0.7rem;
          padding: 3px 8px;
          border-radius: 10px;
          font-weight: 600;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .payment-badge.paid {
          background: rgba(34, 197, 94, 0.15);
          color: #15803d;
        }
        .payment-badge.invoice-sent {
          background: rgba(234, 179, 8, 0.15);
          color: #a16207;
        }
        .payment-badge.not-paid {
          background: rgba(156, 163, 175, 0.15);
          color: #6b7280;
        }

        /* Photo Count Badge */
        .photo-count-badge {
          font-size: 0.7rem;
          padding: 3px 8px;
          border-radius: 10px;
          font-weight: 500;
          white-space: nowrap;
          flex-shrink: 0;
          background: rgba(59, 130, 246, 0.15);
          color: #2563eb;
        }
        .photo-count-badge.clickable {
          cursor: pointer;
          transition: background 0.15s, transform 0.1s;
        }
        .photo-count-badge.clickable:hover {
          background: rgba(59, 130, 246, 0.25);
          transform: scale(1.05);
        }

        /* Mark Paid Link */
        .mark-paid-link {
          font-size: 0.7rem;
          color: #22c55e;
          cursor: pointer;
          text-decoration: underline;
          margin-left: 4px;
        }
        .mark-paid-link:hover {
          color: #16a34a;
        }

        /* Payment Modal */
        .payment-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 250;
          padding: 20px;
        }
        .payment-modal {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 400px;
          overflow: hidden;
        }
        .payment-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #e2e8f0;
        }
        .payment-modal-header h3 {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #64748b;
          padding: 0;
          line-height: 1;
        }
        .payment-modal-body {
          padding: 20px;
        }
        .payment-customer {
          margin: 0 0 16px 0;
          font-size: 0.95rem;
        }
        .payment-address {
          color: #64748b;
        }
        .payment-amount {
          color: #059669;
          font-weight: 600;
        }
        .payment-modal .form-group {
          margin-bottom: 16px;
        }
        .payment-modal .form-group label {
          display: block;
          font-size: 0.85rem;
          font-weight: 500;
          margin-bottom: 6px;
          color: #374151;
        }
        .payment-modal .form-group select,
        .payment-modal .form-group input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.95rem;
        }
        .payment-modal-footer {
          display: flex;
          gap: 12px;
          padding: 16px 20px;
          border-top: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        .payment-modal-footer .btn-cancel {
          flex: 1;
          padding: 10px;
          border: 1px solid #d1d5db;
          background: white;
          border-radius: 8px;
          font-size: 0.95rem;
          cursor: pointer;
        }
        .payment-modal-footer .btn-confirm {
          flex: 1;
          padding: 10px;
          border: none;
          background: #22c55e;
          color: white;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
        }
        .payment-modal-footer .btn-confirm:hover {
          background: #16a34a;
        }
        .payment-modal-footer .btn-confirm:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .available-customers-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 300px;
          overflow-y: auto;
        }
        .available-customer-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: var(--color-background);
          border-radius: 8px;
        }
        .available-customer-item .customer-info {
          flex: 1;
        }
        .available-customer-item .customer-name {
          font-weight: 500;
        }
        .available-customer-item .customer-address {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }

        /* Service Type Badges */
        .service-type-badge {
          font-size: 0.7rem;
          padding: 2px 8px;
          border-radius: 10px;
          font-weight: 600;
          margin-left: 8px;
          white-space: nowrap;
        }
        .service-type-badge.seasonal {
          background: rgba(59, 130, 246, 0.15);
          color: #1d4ed8;
        }
        .service-type-badge.as-needed {
          background: rgba(249, 115, 22, 0.15);
          color: #c2410c;
        }
        .threshold-badge {
          font-size: 0.7rem;
          padding: 2px 8px;
          border-radius: 10px;
          font-weight: 600;
          margin-left: 8px;
          white-space: nowrap;
          background: #e5e7eb;
          color: #374151;
        }

        /* Archive Toggle */
        .routes-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }
        .archive-toggle {
          display: flex;
          gap: 4px;
          background: var(--color-background);
          padding: 4px;
          border-radius: 8px;
          width: fit-content;
        }
        .sort-toggle {
          padding: 8px 14px;
          border: 2px solid var(--color-border);
          border-radius: 20px;
          background: var(--color-card);
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 500;
          color: var(--color-text-light);
          transition: all 0.2s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .sort-toggle:hover {
          border-color: var(--color-primary);
          color: var(--color-primary);
        }
        .toggle-btn {
          padding: 8px 16px;
          border: none;
          background: transparent;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--color-text-light);
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .toggle-btn.active {
          background: white;
          color: var(--color-primary);
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .toggle-btn:hover:not(.active) {
          color: var(--color-text);
        }

        /* Archived Route */
        .route-card.archived {
          opacity: 0.7;
          background: #f9f9f9;
        }
        .archived-badge {
          font-size: 0.7rem;
          background: #6b7280;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          margin-left: 8px;
          font-weight: 500;
          vertical-align: middle;
        }
        .storm-id-badge {
          font-size: 0.75rem;
          background: #3b82f6;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .assigned-badge {
          font-size: 0.75rem;
          background: #8b5cf6;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .assigned-badge.unassigned {
          background: #6b7280;
        }
        .driver-filter-select {
          padding: 6px 12px;
          border: 1px solid #444;
          border-radius: 6px;
          background: #2a2a2a;
          color: #e0e0e0;
          font-size: 0.9rem;
          cursor: pointer;
        }
        .driver-filter-select:hover {
          border-color: #666;
        }

        /* Mobile Menu */
        .mobile-menu-btn {
          display: none;
          flex-direction: column;
          justify-content: space-between;
          width: 24px;
          height: 18px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
        }
        .mobile-menu-btn span {
          display: block;
          width: 100%;
          height: 2px;
          background: white;
          border-radius: 1px;
        }
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-link {
          background: var(--color-secondary);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }

        @media (max-width: 768px) {
          .desktop-nav {
            display: none;
          }
          .mobile-menu-btn {
            display: flex;
          }
          .route-card-header {
            flex-direction: column;
          }
          .route-actions {
            width: 100%;
            flex-wrap: wrap;
          }
          .route-actions .btn {
            flex: 1;
            min-width: 80px;
          }
          /* Mobile payment breakdown - more compact */
          .payment-breakdown {
            font-size: 0.75rem;
            padding: 2px 6px;
          }
        }
      `}</style>

      {/* Photo Gallery Modal */}
      <PhotoGalleryModal
        stopId={photoGalleryStopId}
        isOpen={photoGalleryStopId !== null}
        onClose={() => setPhotoGalleryStopId(null)}
        allowDelete={isAdmin}
        title="Service Photos"
      />

      {/* Payment Modal */}
      {paymentModal.open && paymentModal.stop && (
        <div className="payment-modal-overlay" onClick={() => setPaymentModal({ open: false, stop: null })}>
          <div className="payment-modal" onClick={e => e.stopPropagation()}>
            <div className="payment-modal-header">
              <h3>Mark as Paid</h3>
              <button className="modal-close" onClick={() => setPaymentModal({ open: false, stop: null })}>&times;</button>
            </div>
            <div className="payment-modal-body">
              <p className="payment-customer">
                <strong>{paymentModal.stop.first_name} {paymentModal.stop.last_name}</strong>
                <br />
                <span className="payment-address">{paymentModal.stop.address}</span>
                {!shouldHidePrices() && paymentModal.stop.price && (
                  <span className="payment-amount"> - ${parseFloat(paymentModal.stop.price || 0).toFixed(2)}</span>
                )}
              </p>

              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={paymentForm.paymentMethod}
                  onChange={e => setPaymentForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="venmo">Venmo</option>
                  <option value="cashapp">Cash App</option>
                  <option value="zelle">Zelle</option>
                  <option value="square">Square</option>
                </select>
              </div>

              <div className="form-group">
                <label>Payment Date</label>
                <input
                  type="date"
                  value={paymentForm.paymentDate}
                  onChange={e => setPaymentForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label>Notes (optional)</label>
                <input
                  type="text"
                  value={paymentForm.paymentNotes}
                  onChange={e => setPaymentForm(prev => ({ ...prev, paymentNotes: e.target.value }))}
                  placeholder="e.g., Check #1234"
                />
              </div>
            </div>
            <div className="payment-modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setPaymentModal({ open: false, stop: null })}
              >
                Cancel
              </button>
              <button
                className="btn-confirm"
                onClick={submitPayment}
                disabled={savingPayment}
              >
                {savingPayment ? 'Saving...' : 'Mark Paid'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Routes;
